'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';

function formatDate(dateString) {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
    });
}

function formatDateTime(dateString) {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        hour12: true
    });
}

export function RecentlyViewedBlogs({ currentBlogSlug, currentBlog }) {
    const [recentBlogs, setRecentBlogs] = useState([]);

    useEffect(() => {
        // Get recently viewed blogs from localStorage
        const storedBlogs = JSON.parse(localStorage.getItem('recentlyViewedBlogs') || '[]');
        setRecentBlogs(storedBlogs);

        // If there's a current blog and it's not already in the list, add it
        if (currentBlogSlug && currentBlog) {
            const updatedBlogs = [
                {
                    slug: currentBlogSlug,
                    title: currentBlog.title,
                    authors: currentBlog.authors,
                    date_updated: currentBlog.date_updated,
                    date_created: currentBlog.date_created,
                    viewedAt: new Date().toISOString()
                },
                ...storedBlogs.filter(blog => blog.slug !== currentBlogSlug)
            ].slice(0, 5); // Keep only the 5 most recent

            localStorage.setItem('recentlyViewedBlogs', JSON.stringify(updatedBlogs));
            setRecentBlogs(updatedBlogs);
        }
    }, [currentBlogSlug, currentBlog]);

    if (recentBlogs.length === 0) return null;

    return (
        <div className="mt-12">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-6">Recently Viewed</h3>
            <div className="space-y-6">
                {recentBlogs.map((blog, index) => (
                    <article key={index} className="group">
                        <Link href={`/blog/${blog.slug}`} className="space-y-3">
                            <h4 className="text-sm font-medium text-gray-900 line-clamp-2 group-hover:text-sky-600 transition-colors">
                                {blog.title || blog.slug.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                            </h4>
                            <div className="flex items-center gap-x-4">
                                <div className="flex -space-x-2">
                                    {blog.authors?.map((author, index) => (
                                        author.item && (
                                            <div key={index} className="relative z-1" style={{ zIndex: blog.authors.length - index }}>
                                                {author.item.avatar ? (
                                                    <Image
                                                        src={`${process.env.NEXT_PUBLIC_DIRECTUS_URL}/assets/${author.item.avatar}`}
                                                        alt={`${author.item.first_name} ${author.item.last_name}`}
                                                        width={32}
                                                        height={32}
                                                        className="size-8 rounded-full bg-gray-50 ring-2 ring-white"
                                                    />
                                                ) : (
                                                    <div className="size-8 rounded-full bg-gray-50 ring-2 ring-white flex items-center justify-center">
                                                        <span className="text-gray-500 text-xs">
                                                            {author.item.first_name[0]}{author.item.last_name[0]}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        )
                                    ))}
                                </div>
                                <div className="text-xs">
                                    <p className="font-medium text-gray-900">
                                        {blog.authors?.map((author, index) => (
                                            author.item && (
                                                <span key={index}>
                                                    {`${author.item.first_name} ${author.item.last_name}`}
                                                    {index < blog.authors.length - 1 ? ', ' : ''}
                                                </span>
                                            )
                                        ))}
                                    </p>
                                    <p className="text-gray-500">
                                        {blog.authors?.map((author, index) => (
                                            author.item?.title && (
                                                <span key={index}>
                                                    {author.item.title}
                                                    {index < blog.authors.length - 1 ? ', ' : ''}
                                                </span>
                                            )
                                        ))}
                                    </p>
                                    <div className="flex flex-col gap-1">
                                        <time className="text-gray-500" dateTime={blog.date_updated || blog.date_created}>
                                            Published: {formatDate(blog.date_updated || blog.date_created)}
                                        </time>
                                        <time className="text-gray-500" dateTime={blog.viewedAt}>
                                            Viewed: {formatDateTime(blog.viewedAt)}
                                        </time>
                                    </div>
                                </div>
                            </div>
                        </Link>
                    </article>
                ))}
            </div>
        </div>
    );
} 